import React from "react";
import { CheckCircle2, XCircle, Clock, MinusCircle } from "lucide-react";
import type { LaborAttendanceStatus } from "../../interface/laborAttendance.interface";

// ─── Status options used by the dropdown + cell labels ─────────────────
// Same LaborAttendanceStatus values as before ("--" | "PRESENT" | "ABSENT" | "HALF_DAY")
// label is now plain text — the icon is rendered separately via LABOR_STATUS_ICON_MAP.
export const LABOR_STATUS_OPTIONS: {
  value: LaborAttendanceStatus;
  label: string;
}[] = [
  { value: "PRESENT", label: "Present" },
  { value: "HALF_DAY", label: "Half Day" },
  { value: "ABSENT", label: "Absent" },
  { value: "--", label: "--" },
];

// ─── Bigger, bolder pill colors for each status ─────────────────────────
export const LABOR_STATUS_STYLE_MAP: Record<LaborAttendanceStatus, string> = {
  PRESENT: "bg-emerald-50 text-emerald-700 border-emerald-200",
  HALF_DAY: "bg-amber-50 text-amber-700 border-amber-200",
  ABSENT: "bg-rose-50 text-rose-700 border-rose-200",
  "--": "bg-slate-50 text-slate-400 border-slate-200",
};

// ─── Icon shown before the label, per status ────────────────────────────
export const LABOR_STATUS_ICON_MAP: Record<LaborAttendanceStatus, React.ElementType> = {
  PRESENT: CheckCircle2,
  HALF_DAY: Clock,
  ABSENT: XCircle,
  "--": MinusCircle,
};

// Descriptions shown in the legend row (purely cosmetic, no data impact)
const LEGEND_ITEMS: { status: LaborAttendanceStatus; label: string; desc: string }[] = [
  { status: "PRESENT", label: "Present", desc: "Worked full day" },
  { status: "HALF_DAY", label: "Half Day", desc: "Worked partial day" },
  { status: "ABSENT", label: "Absent", desc: "Did not work" },
  { status: "--", label: "--", desc: "Not marked / Sunday" },
];

const LaborAttendanceLegend: React.FC = () => {
  return (
    <div className="flex flex-wrap items-center gap-2.5">
      {LEGEND_ITEMS.map((item) => {
        const Icon = LABOR_STATUS_ICON_MAP[item.status];
        return (
          <div
            key={item.status}
            title={item.desc}
            className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border-2 text-xs font-bold shadow-sm ${LABOR_STATUS_STYLE_MAP[item.status]}`}
          >
            <Icon className="h-3.5 w-3.5" />
            {item.label}
          </div>
        );
      })}
    </div>
  );
};

export default LaborAttendanceLegend;